import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, CalendarCheck, Menu, X } from "lucide-react";
import { BUSINESS } from "@/lib/api";
import { Button } from "@/components/ui/button";

const NAV = [
  { id: "galerie", label: "Galerie" },
  { id: "prestations", label: "Prestations" },
  { id: "options", label: "Options" },
  { id: "pourquoi", label: "Pourquoi choisir Hingant Clean" },
  { id: "contact", label: "Contact" },
];

const InstagramIcon = ({ className = "h-5 w-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const FacebookIcon = ({ className = "h-5 w-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
  </svg>
);

const GoogleIcon = ({ className = "h-5 w-5" }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.5 5.9 29.6 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.5 5.9 29.6 4 24 4 16.3 4 9.6 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.5 0 10.3-1.9 13.9-5l-6.4-5.4c-2 1.5-4.6 2.4-7.5 2.4-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2 3.7-3.7 5l6.4 5.4C42.6 35.4 44 30.1 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const GOOGLE_REVIEW_URL = "https://g.page/r/CYnLwJG8BuS0EAE/review";

export default function Header({ onOpenBooking }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 bg-white transition-all ${
        scrolled ? "shadow-[0_6px_28px_-14px_rgba(11,58,91,0.25)]" : "shadow-none"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center shrink-0" data-testid="header-logo-link">
          <img
            src={BUSINESS.logo}
            alt="Hingant Clean — Nettoyage automobile à domicile"
            className="h-24 sm:h-32 lg:h-36 w-auto object-contain"
          />
        </Link>

        {isHome && (
          <nav className="hidden xl:flex items-center gap-6">
            {NAV.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                data-testid={`nav-${n.id}`}
                className="text-[15px] font-medium text-[#2F3438]/80 hover:text-[#0B3A5B] transition-colors"
              >
                {n.label}
              </a>
            ))}
          </nav>
        )}

        <div className="hidden md:flex items-center gap-2.5">
          {/* Social — promoted, bigger, premium */}
          <a
            href={BUSINESS.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-instagram"
            aria-label="Instagram Hingant Clean"
            className="group relative h-12 w-12 rounded-full bg-gradient-to-br from-[#feda75] via-[#d62976] to-[#4f5bd5] text-white inline-flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-[0_10px_30px_-8px_rgba(214,41,118,0.6)] ring-2 ring-white"
          >
            <InstagramIcon className="h-5 w-5" />
          </a>
          <a
            href={BUSINESS.facebook}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-facebook"
            aria-label="Facebook Hingant Clean"
            className="group relative h-12 w-12 rounded-full bg-[#1877F2] text-white inline-flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-[0_10px_30px_-8px_rgba(24,119,242,0.6)] ring-2 ring-white"
          >
            <FacebookIcon className="h-5 w-5" />
          </a>
          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-google-review"
            aria-label="Laisser un avis Google"
            title="Laisser un avis Google"
            className="group relative h-12 w-12 rounded-full bg-white text-[#0B3A5B] inline-flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-[0_10px_30px_-8px_rgba(66,133,244,0.5)] ring-2 ring-white border border-[#0B3A5B]/10 mr-1"
          >
            <GoogleIcon className="h-5 w-5" />
          </a>
          <a
            href={`tel:${BUSINESS.phone}`}
            data-testid="header-call-button"
            className="inline-flex items-center gap-2 h-12 px-4 rounded-full border border-[#0B3A5B]/15 text-[#0B3A5B] font-semibold hover:bg-[#F3F5F7] transition-colors"
          >
            <Phone className="h-4 w-4" />
            <span className="text-sm hidden xl:inline">{BUSINESS.phoneDisplay}</span>
            <span className="text-sm xl:hidden">Appeler</span>
          </a>
          <Button
            data-testid="header-booking-button"
            onClick={onOpenBooking}
            className="h-12 px-5 rounded-full bg-[#1E88C8] hover:bg-[#1670a8] text-white font-semibold"
          >
            <CalendarCheck className="h-4 w-4 mr-2" />
            Prendre RDV
          </Button>
        </div>

        {/* Mobile actions */}
        <div className="md:hidden flex items-center gap-1.5">
          <a
            href={BUSINESS.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-instagram-mobile"
            aria-label="Instagram"
            className="h-11 w-11 rounded-full bg-gradient-to-br from-[#feda75] via-[#d62976] to-[#4f5bd5] text-white inline-flex items-center justify-center ring-2 ring-white shadow"
          >
            <InstagramIcon className="h-4 w-4" />
          </a>
          <a
            href={BUSINESS.facebook}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-facebook-mobile"
            aria-label="Facebook"
            className="h-11 w-11 rounded-full bg-[#1877F2] text-white inline-flex items-center justify-center ring-2 ring-white shadow"
          >
            <FacebookIcon className="h-4 w-4" />
          </a>
          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-google-review-mobile"
            aria-label="Laisser un avis Google"
            className="h-11 w-11 rounded-full bg-white text-[#0B3A5B] inline-flex items-center justify-center ring-2 ring-white shadow border border-[#0B3A5B]/10"
          >
            <GoogleIcon className="h-4 w-4" />
          </a>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            className="inline-flex items-center justify-center h-11 w-11 rounded-full border border-[#0B3A5B]/15 text-[#0B3A5B] ml-0.5"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#0B3A5B]/10 bg-white">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-2">
            {isHome &&
              NAV.map((n) => (
                <a
                  key={n.id}
                  href={`#${n.id}`}
                  data-testid={`mobile-nav-${n.id}`}
                  className="py-2 text-[15px] font-medium text-[#2F3438]"
                  onClick={() => setOpen(false)}
                >
                  {n.label}
                </a>
              ))}
            <div className="flex gap-2 mt-2">
              <a
                href={`tel:${BUSINESS.phone}`}
                data-testid="mobile-call-button"
                className="flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-full border border-[#0B3A5B]/15 text-[#0B3A5B] font-semibold"
              >
                <Phone className="h-4 w-4" /> Appeler
              </a>
              <Button
                data-testid="mobile-booking-button"
                onClick={() => {
                  setOpen(false);
                  onOpenBooking();
                }}
                className="flex-1 h-12 rounded-full bg-[#1E88C8] hover:bg-[#1670a8] text-white font-semibold"
              >
                <CalendarCheck className="h-4 w-4 mr-2" /> RDV
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
